import { Command } from "clipanion";
import { renderError } from "./render-error.js";
import type { OutputMode } from "./output.js";

/**
 * Base class for all m8t CLI commands.
 *
 * Wraps the subclass's `executeCommand()` in try/catch + `renderError()` so
 * any thrown error (LocalCliError, ApiCallError, ExitPromptError, raw Error)
 * is rendered through our friendly hint table + the right exit code is
 * returned — instead of bubbling out to clipanion's default error handler
 * which writes a raw stack trace to stderr (intercepting it BEFORE our
 * cli.ts top-level catch can run).
 *
 * Subclasses implement `executeCommand()` instead of `execute()`. The base
 * class's `execute()` is final-by-convention (do not override).
 *
 * The `verbose` + `output` flags are read from process.argv (not from the
 * command's own `Option.String("--verbose")` / `Option.String("--output")`)
 * to handle the case where the command class doesn't declare those flags
 * but the user still passed them (e.g., `m8t version --verbose`). Both
 * flags are widely-applicable so this fallback is acceptable.
 */
export abstract class M8tCommand extends Command {
  abstract executeCommand(): Promise<number>;

  async execute(): Promise<number> {
    try {
      return await this.executeCommand();
    } catch (e) {
      const verbose = process.argv.includes("--verbose");
      const outputIdx = process.argv.indexOf("--output");
      const output: OutputMode | undefined =
        outputIdx >= 0 && outputIdx + 1 < process.argv.length
          ? (process.argv[outputIdx + 1] as OutputMode)
          : undefined;
      return renderError(e, {
        stderr: this.context.stderr as NodeJS.WriteStream,
        verbose,
        output,
      });
    }
  }
}
